import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Router, NavigationStart } from '@angular/router';
import { AuthUser, getCurrentUser, signOut } from 'aws-amplify/auth';
import { Apiservice } from './ApiService';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService implements OnDestroy {
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  isLoggedIn = this.isLoggedInSubject.asObservable();
  public adminSubject = new BehaviorSubject<boolean>(false);
  admin = this.adminSubject.asObservable();
  public deliveryAdminSubject = new BehaviorSubject<boolean>(false);
  deliveryAdmin = this.deliveryAdminSubject.asObservable();
  public ticketmasterSubject = new BehaviorSubject<boolean>(false);
  ticketmaster = this.ticketmasterSubject.asObservable();
  public venueAdminSubject = new BehaviorSubject<boolean>(false);
  venueAdmin = this.venueAdminSubject.asObservable();
  public globalAdminSubject = new BehaviorSubject<boolean>(false);
  globalAdmin = this.globalAdminSubject.asObservable();
  public scanSubject = new BehaviorSubject<boolean>(false);
  scan = this.scanSubject.asObservable();
  primaryVenue: string = ''

  
  
  private routerSubscription: Subscription;

  // Routes that are publicly accessible (no AuthGuard) and therefore have no
  // authenticated user — the /user/ permission lookup must be skipped for them.
  private readonly publicRoutePrefixes = ['/share'];

  constructor(private apiService: Apiservice, private router: Router) {
    this.checkLoginStatus();
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart && !this.isPublicRoute(event.url)) {
        this.checkPermissions();
      }
    });
  }

  // Treats a path as public if it matches a known public prefix. Falls back to
  // window.location so cold loads are classified correctly even before the
  // router has resolved its own url.
  private isPublicRoute(url?: string): boolean {
    const path = (url ?? window.location.pathname) || '';
    return this.publicRoutePrefixes.some(
      (prefix) => path === prefix || path.startsWith(prefix + '?') || path.startsWith(prefix + '/')
    );
  }

  private async checkLoginStatus() {
    try {
      const user = await getCurrentUser();
      this.isLoggedInSubject.next(!!user);
    } catch {
      this.isLoggedInSubject.next(false);
    }
  }

  async refreshUser() {
    this.checkLoginStatus();
  }

  async checkPermissions() {
    // Public routes (e.g. the shared report) have no authenticated user, so the
    // /user/ lookup would only 401. Skip it.
    if (this.isPublicRoute()) {
      return;
    }
    try {
      const result: any = await firstValueFrom(this.apiService.getCurrentUser());
      this.adminSubject.next(result.permissions.includes('all'));
      this.deliveryAdminSubject.next(result.permissions.includes('deliveryAdmin'));
      this.ticketmasterSubject.next(result.primaryVenue === 'ticketmaster');
      this.venueAdminSubject.next(result.venueAdmin === true);
      this.globalAdminSubject.next(result.permissions.includes('globalAdmin'));
      this.scanSubject.next(result.permissions.includes('scan'));
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  }
  

  async signOut() {
    try {
      await signOut(); 
      this.isLoggedInSubject.next(false);
      this.adminSubject.next(false);
      this.deliveryAdminSubject.next(false);
      this.ticketmasterSubject.next(false);
      this.venueAdminSubject.next(false);
      this.globalAdminSubject.next(false);
      this.scanSubject.next(false);
  
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }
  
  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
